import Config from '@config';
import { initializeEmployeesApi } from '@gasbi/shared/common/features/employees';
import { Employee } from '@gasbi/shared/common/features/employees';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  EmployeeUniqueInfo,
  UpdateEmployeeAccessRequest,
  UpdateEmployeeRequest,
} from './types';

export const {
  employeesApi,
  useGetEmployeesByRoleQuery,
  useGetEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeByIdMutation,
  useActivateEmployeeByIdMutation,
  useResetEmployeePasswordByIdMutation,
  useDeactivateEmployeeByIdMutation,
  useLazyGetEmployeeByEmailQuery,
  useGetEmployeeByIdentityDetailsQuery,
  useActivateEmployeeByIdentityIdMutation,
  useResetPasswordEmployeeByIdentityIdMutation,
  useDeactivateEmployeeByIdentityIdMutation,
  useGetEmployeesInfiniteQuery,
  useGetEmployeesByIdentityInfiniteQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesAllInfiniteQuery,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeeByIdentityIdSelector,
} = initializeEmployeesApi({
  baseUrl: Config.VITE_API_URL,
  baseQuery: axiosBaseQuery,
});

const extendedEmployeesApi = employeesApi.injectEndpoints({
  endpoints: builder => ({
    getEmployeeGroup: builder.query<Employee[], string>({
      query: identityId => ({
        url: '/query',
        method: 'GET',
        params: { filter: `identityId=${identityId}`, pageSize: 100 },
      }),
      providesTags: () => ['Employees'],
    }),
    getEmployeeGroupByOrganization: builder.query<
      Employee[],
      { identityId: string; organizationId: string }
    >({
      query: ({ identityId, organizationId }) => ({
        url: '/query',
        method: 'GET',
        params: {
          filter: `identityId=${identityId}, organizationId=${organizationId}`,
          pageSize: 100,
        },
      }),
      providesTags: () => ['Employees', 'AllEmployeeRole'],
    }),
    getUniqueUsers: builder.query<
      EmployeeUniqueInfo[],
      { organizationId?: string | null } | void
    >({
      query: params => ({
        url: '/users',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: () => ['Employees', 'EmployeesAll'],
    }),
    updateEmployee: builder.mutation<string, UpdateEmployeeRequest>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Employees',
        { type: 'Employee', id },
      ],
    }),
    updateEmployeeAccess: builder.mutation<
      string[],
      UpdateEmployeeAccessRequest
    >({
      query: data => ({
        url: `/${data.id}/access`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'Employees',
        'AllEmployeeRole',
        { type: 'Employee', id },
      ],
    }),
    activateEmployee: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        'Employees',
        'AllEmployeeRole',
        { type: 'Employee', id },
      ],
    }),
    activateEmployeesByIdentity: builder.mutation<void, { identityId: string }>(
      {
        query: params => ({
          url: '/activate-by-identity',
          method: 'POST',
          params,
        }),
        invalidatesTags: () => ['Employees', 'EmployeesAll', 'AllEmployeeRole'],
      },
    ),
  }),
});

export const {
  useGetEmployeeGroupQuery,
  useGetEmployeeGroupByOrganizationQuery,
  useGetUniqueUsersQuery,
  useUpdateEmployeeMutation,
  useUpdateEmployeeAccessMutation,
  useActivateEmployeeMutation,
  useActivateEmployeesByIdentityMutation,
} = extendedEmployeesApi;
